import _ from 'lodash'
import { Link, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Card, CardActions, CardContent, CardMedia, Grid, Stack, Theme, ThemeProvider, Typography, createTheme } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import { LoadingButton } from '@mui/lab'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'

import defaultNetworkCover from '@/assets/img/network-cover.png'

import PageContainer from '@/components/ui/containers/PageContainer'

import { useGetMyNetworkInvitationsQuery } from '@/core/services/auth'
import { useReplyNetworkInvitationMutation } from '@/core/services/network'

import { getApiErrorMessage } from '@/utils/errors'
import { isConsultationOrder, isExpertiseOrder } from '@/utils/type-guards'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export default function InvitationNetworks() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const { data: networkInvitations } = useGetMyNetworkInvitationsQuery({ skip: 0 }, { refetchOnMountOrArgChange: true })
  const [replyNetworkInvitation, { isLoading: isReplyingNetworkInvitation, originalArgs: replyNetworkInvitationArgs }] = useReplyNetworkInvitationMutation()

  return (
    <Stack px={isMobile ? 4 : 8} py={isMobile ? 2 : 4} spacing={3} width="100%">
      <Stack direction="row">
        <Typography component="h2" sx={{ fontSize: 30, fontWeight: 700 }}>
          {t('page.networks.invitationNetwork.title')}
        </Typography>
      </Stack>
      <ThemeProvider
        theme={(theme: Theme) =>
          createTheme({
            ...theme,
            components: {
              ...theme.components,
              MuiSvgIcon: { defaultProps: { fontSize: 'small' }, styleOverrides: { root: { color: theme.palette.grey[500] } } },
            },
          })
        }
      >
        <PageContainer>
          <Grid container spacing={2}>
            {_.map(networkInvitations, (networkInvitation) => (
              <Grid key={networkInvitation._id} item xs={12}>
                <Card sx={{ minWidth: 275 }}>
                  <Stack direction="row" spacing={2}>
                    <CardMedia component="img" sx={{ width: 200 }} image={networkInvitation.network.coverPhoto?.url ?? defaultNetworkCover} alt="cover-photo" />
                    <Stack direction="column" flex={1}>
                      <CardContent>
                        <Typography
                          gutterBottom
                          variant="h5"
                          component={Link}
                          to={`/networks/${networkInvitation.network._id}`}
                          sx={{
                            display: 'inline-block',
                            marginBottom: '8px !important',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {networkInvitation.network.name}
                        </Typography>
                        <Stack direction="row" alignItems="center" minHeight="30px" spacing={1}>
                          <GroupRoundedIcon />
                          <Typography variant="body2" color="text.secondary">
                            {t('page.networks.invitationNetwork.memberCount', { count: networkInvitation.network.members.length })}
                          </Typography>
                        </Stack>
                        {[
                          { count: networkInvitation.network.pendingOrders.filter(isConsultationOrder).length, type: 'consultation' },
                          { count: networkInvitation.network.pendingOrders.filter(isExpertiseOrder).length, type: 'expertise' },
                        ].map(({ count, type }, index) => (
                          <Stack key={index} direction="row" alignItems="center" minHeight="30px" spacing={1}>
                            <HourglassBottomRoundedIcon />
                            <Typography variant="body2" color="text.secondary">
                              {t('page.networks.invitationNetwork.pendingOrder', { count, context: type })}
                            </Typography>
                          </Stack>
                        ))}
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Stack
                          flex={1}
                          bgcolor={blueGrey[100]}
                          borderRadius={2}
                          direction={{ xs: 'column', lg: 'row' }}
                          alignItems={{ xs: 'start', lg: 'center' }}
                          justifyContent="space-between"
                          p={2}
                          spacing={2}
                        >
                          <Typography component="span" sx={{ fontWeight: 700 }}>
                            {t('page.networks.invitationNetwork.invitedBy', {
                              inviter: `${networkInvitation.inviter.userInfo.name} ${networkInvitation.inviter.userInfo.surname}`,
                            })}
                          </Typography>
                          <Stack direction="row" spacing={2}>
                            <LoadingButton
                              loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'accept'}
                              onClick={async () => {
                                try {
                                  await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'accept' }).unwrap()
                                  navigate(`/networks/${networkInvitation.network._id}`)
                                } catch (error) {
                                  enqueueSnackbar(getApiErrorMessage(error, t('notification.network.acceptInvitation.error')), { variant: 'error' })
                                }
                              }}
                              size="small"
                              variant="contained"
                            >
                              {t('common:button.join.label')}
                            </LoadingButton>
                            <LoadingButton
                              loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'decline'}
                              onClick={async () => {
                                try {
                                  await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'decline' }).unwrap()
                                } catch (error) {
                                  enqueueSnackbar(getApiErrorMessage(error, t('notification.network.declineInvitation.error')), { variant: 'error' })
                                }
                              }}
                              size="small"
                              variant="outlined"
                            >
                              {t('common:button.decline.label')}
                            </LoadingButton>
                          </Stack>
                        </Stack>
                      </CardActions>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </PageContainer>
      </ThemeProvider>
    </Stack>
  )
}
